"use client"

import { FormEvent, KeyboardEvent, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Loader2Icon, Send } from "lucide-react"
import { useBranding } from "@/lib/branding"

interface ChatInputProps {
  input: string
  setInput: (input: string) => void
  handleSubmit: (e: FormEvent) => void
  isLoading: boolean
  className?: string
}

export function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  className = "",
}: ChatInputProps) {
  const branding = useBranding()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize the textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "0px"
      const scrollHeight = textarea.scrollHeight
      textarea.style.height = scrollHeight + "px"
    }
  }, [input])

  // Handle key presses for Ctrl+Enter to add new line and Enter to submit
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.ctrlKey) {
        // Add a new line when Ctrl+Enter is pressed
        setInput(`${input}\n\n`)
        e.preventDefault()
      } else if (!e.shiftKey) {
        // Submit the form when Enter is pressed without Shift
        if (input.trim()) {
          e.preventDefault()
          handleSubmit(e as unknown as FormEvent)
        }
      }
    }
  }

  return (
    <div className={`p-4 w-full ${className}`}>
      {/* The composer floats at e2 and rises to e3 on focus; the border and
          the accent rail carry the focus colour so the shadow can stay ink. */}
      <form
        onSubmit={handleSubmit}
        className="group relative flex w-full items-end gap-2 rounded-2xl border border-border bg-card p-2.5 shadow-e2 transition-[border-color,box-shadow] duration-200 ease-out-quiet focus-within:border-brand-coral/60 focus-within:shadow-e3"
      >
        {/* Left accent rail: fades in on focus */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-brand-coral opacity-0 transition-opacity group-focus-within:opacity-100"
        />

        <Textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask ${branding.productName} anything…   ⌘ Enter for a new line`}
          disabled={isLoading}
          className="min-h-[44px] max-h-[200px] flex-1 resize-none border-0 bg-transparent py-2.5 pl-3 text-base leading-relaxed text-brand-ink placeholder:text-muted-foreground/70 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          rows={1}
          autoFocus
        />

        <Button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="h-11 min-w-11 rounded-xl bg-brand-ink px-4 text-brand-mist shadow-none transition-[background-color,transform] duration-200 ease-out-quiet hover:bg-brand-coral active:scale-[0.98] focus-visible:ring-brand-coral/50 disabled:bg-secondary disabled:text-muted-foreground"
        >
          {isLoading ? (
            <>
              <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" />
              <span className="font-medium">Thinking</span>
            </>
          ) : (
            <>
              <span className="font-medium">Send</span>
              <Send className="ml-1.5 h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      <p className="mt-2.5 px-2 text-[11px] tracking-wide text-muted-foreground/70">
        {branding.productName} can use tools and may make mistakes. Check anything important before
        acting on it.
      </p>
    </div>
  )
}
